import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Clock, ArrowUpRight, MapPin, Gift, HeartHandshake, Search, X, Flower2 } from 'lucide-react';
import { BLOG_POSTS } from '../mock';
import Seo, { SITE } from '../components/Seo';
import Reveal from '../components/Reveal';

const getCategory = (post) => {
  if (/flowers-in-season-/.test(post.slug)) return 'seasonal';
  if (/funeral|sympathy/.test(post.slug)) return 'sympathy';
  if (/-ca-florist|same-day-flower-delivery/.test(post.slug)) return 'city';
  return 'occasions';
};

const CATEGORIES = [
  { id: 'city', title: 'City & Local Delivery Guides', desc: 'Same-day flower delivery guides for every town we serve on the Peninsula.', Icon: MapPin },
  { id: 'occasions', title: 'Occasions & Flower Guides', desc: 'Evergreen inspiration for anniversaries, birthdays, weddings, and more.', Icon: Gift },
  { id: 'seasonal', title: 'Monthly Bloom Guides', desc: 'What\u2019s in season every month of the year in Northern California — with birth flowers and florist tips.', Icon: Flower2 },
  { id: 'sympathy', title: 'Sympathy & Funeral Flowers', desc: 'Gentle, practical guidance for tributes, etiquette, and difficult days.', Icon: HeartHandshake },
];

export default function BlogListPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();
  const matches = (p) => !q || `${p.title} ${p.excerpt} ${p.keywords}`.toLowerCase().includes(q);
  const filtered = BLOG_POSTS.filter(matches);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      '@id': `${SITE}/blog`,
      name: 'Flower Delivery Blog | Tips, Occasions & Local Guides',
      description: 'Friendly guides from a local Peninsula florist: same-day delivery guides for Atherton, Menlo Park, Redwood City & Palo Alto, plus occasion and etiquette tips.',
      url: `${SITE}/blog`,
      publisher: { '@type': 'Organization', name: 'Menlo Park Florist', url: SITE },
      blogPost: BLOG_POSTS.map((p) => ({
        '@type': 'BlogPosting',
        headline: p.title,
        url: `${SITE}/blog/${p.slug}`,
        datePublished: p.date,
        image: p.heroImg,
        author: { '@type': 'Person', name: p.author },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE}/blog` },
      ],
    },
  ];

  return (
    <>
      <Seo
        title="Flower Delivery Blog | Tips, Occasions & Local Guides"
        description="Friendly guides and tips from your local Bay Area florist — including same-day flower delivery in Atherton, Menlo Park and the Peninsula, occasion ideas, and flower care."
        path="/blog"
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#E9D4CE]/50 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-10 lg:pt-20">
          <span className="text-[13px] tracking-[0.3em] uppercase text-[#C4715A] font-medium">The Journal</span>
          <h1 className="font-serif-display text-[42px] sm:text-[54px] text-[#26302a] mt-3 leading-[1.05] max-w-2xl">Fresh ideas from your local florist</h1>
          <p className="mt-5 text-[17px] text-[#5A554C] max-w-xl leading-relaxed">Local delivery guides, occasion inspiration, and tips to keep your blooms beautiful for longer.</p>
          <div className="mt-8 relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 w-[18px] h-[18px] text-[#8A7C6A]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search articles…"
              data-testid="blog-search-input"
              className="w-full rounded-full border border-[#DCD3C3] bg-white pl-11 pr-11 py-3 text-[15px] text-[#26302a] placeholder:text-[#A79A87] focus:outline-none focus:border-[#3D5142] transition-colors"
            />
            {query && (
              <button type="button" data-testid="blog-search-clear" onClick={() => setQuery('')} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-[#EDE7DC] transition-colors">
                <X className="w-4 h-4 text-[#5A554C]" />
              </button>
            )}
          </div>
          {q ? (
            <p data-testid="blog-search-count" className="mt-4 text-[14px] text-[#5A554C]">{filtered.length} article{filtered.length === 1 ? '' : 's'} matching “{query.trim()}”</p>
          ) : (
          <div data-testid="category-jump-links" className="mt-8 flex flex-wrap gap-3">
            {CATEGORIES.map(({ id, title, Icon }) => (
              <button
                key={id}
                type="button"
                data-testid={`jump-link-${id}`}
                onClick={() => document.getElementById(`category-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="inline-flex items-center gap-2 rounded-full border border-[#DCD3C3] bg-white px-5 py-2.5 text-[14px] font-medium text-[#3D5142] hover:border-[#3D5142] hover:text-[#C4715A] transition-colors"
              >
                <Icon className="w-4 h-4 text-[#C4715A]" /> {title}
              </button>
            ))}
          </div>
          )}
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 space-y-16">
          {q && filtered.length === 0 && (
            <div data-testid="blog-search-empty" className="text-center py-16">
              <p className="font-serif-display text-[26px] text-[#26302a]">No articles found</p>
              <p className="mt-2 text-[15px] text-[#5A554C]">Try a different keyword, like a city name or occasion.</p>
            </div>
          )}
          {CATEGORIES.map(({ id, title, desc, Icon }) => {
            const posts = filtered.filter((p) => getCategory(p) === id);
            if (!posts.length) return null;
            return (
              <div key={id} id={`category-${id}`} data-testid={`blog-category-${id}`} className="scroll-mt-28">
                <Reveal>
                  <div className="flex items-start gap-4 mb-8">
                    <span className="mt-1 inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#EDE7DC] border border-[#E1D7C7]"><Icon className="w-5 h-5 text-[#C4715A]" /></span>
                    <div>
                      <h2 className="font-serif-display text-[30px] text-[#26302a] leading-tight">{title}</h2>
                      <p className="mt-1 text-[15px] text-[#5A554C]">{desc}</p>
                    </div>
                  </div>
                </Reveal>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {posts.map((post, i) => (
                    <Reveal key={post.slug} delay={(i % 3) * 80}>
                      <Link to={`/blog/${post.slug}`} data-testid={`blog-card-${post.slug}`} className="group flex flex-col bg-white rounded-2xl overflow-hidden border border-[#EFE8DD] hover:shadow-[0_20px_50px_rgba(61,81,66,0.15)] hover:-translate-y-1.5 transition-all duration-500 h-full">
                        <div className="relative overflow-hidden aspect-[16/10] bg-[#F4EFE7]">
                          <img src={post.heroImg} alt={post.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                        </div>
                        <div className="p-6 flex flex-col flex-1">
                          <div className="flex items-center gap-4 text-[12.5px] text-[#8A7C6A] mb-3">
                            <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> {post.dateDisplay}</span>
                            <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {post.readTime}</span>
                          </div>
                          <h3 className="font-serif-display text-[22px] text-[#2A2A28] leading-snug">{post.title}</h3>
                          <p className="mt-3 text-[14.5px] text-[#5A554C] leading-relaxed flex-1">{post.excerpt}</p>
                          <span className="mt-5 inline-flex items-center gap-1.5 text-[14px] font-medium text-[#3D5142] group-hover:text-[#C4715A] transition-colors">Read more <ArrowUpRight className="w-4 h-4" /></span>
                        </div>
                      </Link>
                    </Reveal>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}
